<template>
    <div class="profile-data" v-if="profile">
        <form class="form" @keyup.esc="resetForm" @keypress.enter="submitForm" @submit.prevent="submitForm">
            <slot/>

            <fieldset v-if="$current.profile == 'Pier14' && (isManager || Object.keys(hrDocuments).length)">
                <legend>HR Dokumente</legend>

                <div class="fieldset--inner">

                    <!-- HR UPLOADED DOCUMENTS -->
                    <div class="profile--hr-documents" v-for="(documents, key) in hrDocuments" :key="key">
                        <div class="row" v-for="(field, index) in documents" :key="index">
                            <label for="document">
                                <span>{{ getDocumentGroup(key) }}</span>
                            </label>
                            <div class="form-input">
                                <div class="form-text" v-if="isUploaded(key)">
                                    <a href="#" @click.prevent="downloadFile(key, index)">{{ field.name }}</a>
                                    <icon name="delete" @click.native.prevent="deleteUpload(key, index)"
                                          v-if="isManager"/>
                                    <br>
                                    <small>{{ getDocumentValue(key) }}, {{ field.date }}</small>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- HR DOCUMENT TYPE SELECTION -->
                    <div class="row" v-if="addDocument">
                        <div class="dropdown dropdown--framed">
                            <span class="label">
                                <span>Dokumenttyp</span>
                            </span>
                            <div>
                                <icon name="chevron_down" viewBox="0 0 24 24"/>
                                {{ getDocumentValue(documentType) }}
                                <select id="document_type" v-model="documentType">
                                    <optgroup :label="group.label" :key="group.key" v-for="group in documentTypes">
                                        <option :value="getDocumentKey(group.key, doc.key)"
                                                v-for="doc in group.documents">{{ doc.label }}
                                        </option>
                                    </optgroup>
                                </select>
                            </div>
                        </div>
                    </div>

                    <!-- HR UPLOAD -->
                    <div class="row" v-if="documentType">
                        <label for="document">
                            <span>Dokument</span>
                        </label>
                        <div class="form-text">
                            <input id="document" type="file" @change="uploadFile($event, 'document')"><br><br>
                            <small><a href="#" @click.prevent="cancelDocumentUpload()">abbrechen</a></small>
                        </div>
                    </div>

                    <!-- HR ADD DOCUMENT -->
                    <div class="row" v-if="isManager && !addDocument">
                        <br>
                        <action @click.native="addDocument = true" color="blue" v-if="!addDocument">
                            {{ $t('common.button.add') }}
                        </action>
                    </div>
                </div>
            </fieldset>
            
            <fieldset v-for="fieldset in profile.template" v-if="hasFields(fieldset)">
                <legend>{{ fieldset.name[$lang] }}</legend>

                <small v-if="typeof fieldset.hidden !== 'undefined'">
                    <a href="#" @click.prevent="fieldset.hidden = false"
                       v-if="fieldset.hidden">{{ $t('text.show_details') }}</a>
                    <a href="#" @click.prevent="fieldset.hidden = true" v-else>{{ $t('text.hide_details') }}</a>
                </small>

                <div class="note" v-if="fieldset.note" style="margin-top:20px">{{ fieldset.note[$lang] }}</div>

                <div :class="['fieldset--inner', fieldset.hidden ? 'is-hidden': null]">
                    <div :class="getClasses(field)" v-for="field in fieldset.fields" v-if="showField(field)">
                        <hr v-if="field.type == 'divider'"/>

                        <!-- LABEL -->
                        <label :for="field.id" v-if="showLabel(field)">
                            <span v-html="label(field)"></span>
                            <icon name="info" v-tooltip="field.description[$lang]" v-if="field.description"/>
                        </label>

                        <!-- TEXT -->
                        <div class="form-input" :class="{ error: $v.hasError(field.id) }" v-if="field.type == 'text'"
                             :style="field.width ? 'width:' + field.width : null">
                            <input :id="field.id" type="text" v-model="profile.values[field.id]"
                                   :disabled="isDisabled(field)">
                            <error :message="$v.message(field.id)"/>
                        </div>

                        <!-- TEXTAREA -->
                        <div class="form-input" :class="{ error: $v.hasError(field.id) }"
                             v-if="field.type == 'textarea'" :style="field.width ? 'width:' + field.width : null">
                            <textarea :id="field.id" v-model="profile.values[field.id]" :disabled="isDisabled(field)"
                                      @keypress.enter.stop :readonly="isReadonly(field)"></textarea>
                            <error :message="$v.message(field.id)"/>
                        </div>

                        <!-- EMAIL -->
                        <div class="form-input" :class="{ error: $v.hasError(field.id) }" v-if="field.type == 'email'">
                            <input :id="field.id" type="email" v-model="profile.values[field.id]"
                                   :disabled="isDisabled(field)">
                            <error :message="$v.message(field.id)"/>
                        </div>

                        <!-- DATE -->
                        <div class="form-input" :class="{ error: $v.hasError(field.id) }" v-if="field.type == 'date'"
                             style="width:160px">
                            <pikaday :id="field.id" :date="profile.values[field.id]" @change="updateDate"
                                     :disabled="isDisabled(field)"/>
                            <icon name="info" v-tooltip="$t('tooltip.date_info')" class="date-info"/>
                            <error :message="$v.message(field.id)"/>
                        </div>

                        <!-- NUMBER -->
                        <div class="form-input" :class="{ error: $v.hasError(field.id) }" v-if="field.type == 'number'"
                             :style="field.width ? 'width:' + field.width : null">
                            <input :id="field.id" type="number" :step="field.step ? field.step : 1"
                                   v-model="profile.values[field.id]" :disabled="isDisabled(field)">
                            <div
                                v-if="$current.profile == 'Gieck' && field.id === 'contract_hours' && profile.values[field.id]"
                                style="margin-top: 5px; font-size: 0.9em; color: #666;">
                                <div>Pro Woche: {{ (profile.values[field.id] / 52).toFixed(2) }}</div>
                                <div>Pro Monat: {{ (profile.values[field.id] / 12).toFixed(2) }}</div>
                            </div>
                            <error :message="$v.message(field.id)"/>
                        </div>

                        <!-- UPLOAD -->
                        <div class="form-input" :class="{ error: $v.hasError(field.id) }" v-if="field.type == 'upload'">
                            <div class="form-text" v-if="isUploaded(field.id)">
                                <a href="#" @click.prevent="downloadFile(field.id, 0)">{{
                                        uploads[field.id][0].name
                                    }}</a>
                                <icon name="delete" @click.native.prevent="deleteUpload(field.id, 0)"/>
                            </div>
                            <input :id="field.id" type="file" @change="uploadFile($event, field)"
                                   :disabled="isDisabled(field)" v-else>
                            <error :message="$v.message(field.id)"/>
                        </div>

                        <!-- MULTI UPLOAD -->
                        <div class="form-input" :class="{ error: $v.hasError(field.id) }"
                             v-if="field.type == 'multi-upload'">

                            <!-- UPLOADS -->
                            <div v-for="(file, index) in uploads[field.id]" :key="index">
                                <div class="form-text" v-if="isUploaded(field.id)">
                                    <a href="#" @click.prevent="downloadFile(field.id, index)">{{ file.name }}</a>
                                    <icon name="delete" @click.native.prevent="deleteUpload(field.id, index)"/>
                                    <br>
                                </div>
                            </div>

                            <!-- UPLOAD -->
                            <div v-if="addUpload[field.id] === true || ! uploads[field.id]">
                                <input :id="field.id" type="file" @change="uploadFile($event, field)"
                                       :disabled="isDisabled(field)">
                                <error :message="$v.message(field.id)"/>
                            </div>

                            <!-- ADD BUTTON -->
                            <div
                                v-if="uploads[field.id] && (addUpload[field.id] === false || addUpload[field.id] === undefined)">
                                <action @click.native="$set(addUpload, field.id, true)" color="blue"
                                        class="button--small" style="margin-left:0">+ {{ $t('common.button.add') }}
                                </action>
                            </div>

                        </div>

                        <!-- CHECKBOX -->
                        <div class="form-checkbox" :class="{ error: $v.hasError(field.id) }"
                             v-if="field.type == 'checkbox'">
                            <label>
                                <input type="checkbox" :id="field.id" v-model="profile.values[field.id]" value="1"
                                       :disabled="isDisabled(field)">
                                <span v-html="label(field)"></span>
                            </label>
                            <error :message="$v.message(field.id)"/>
                        </div>

                        <!-- TAGS -->
                        <div class="form-input form-input-tags" :class="{ error: $v.hasError(field.id) }"
                             v-if="field.type == 'auto'" @keypress.enter.stop>
                            <v-select v-model="selection[field.id]" :options="getCollection(field)"
                                      @input="setValue(field)" :disabled="isDisabled(field)">
                                <span slot="no-options">{{ $t('text.no_results') }}</span>
                            </v-select>
                            <error :message="$v.message(field.id)"/>
                        </div>

                        <!-- RADIO -->
                        <div class="form-radio" :class="{ error: $v.hasError(field.id) }" v-if="field.type == 'radio'">
                            <label v-for="(option, key) in field.options">
                                <input type="radio" :id="field.id + '_' + key" v-model="profile.values[field.id]"
                                       :value="key" :disabled="isDisabled(field)">
                                <span>{{ option }}</span>
                            </label>
                            <error :message="$v.message(field.id)"/>
                        </div>

                        <!-- YES/NO -->
                        <div class="form-radio" :class="{ error: $v.hasError(field.id) }" v-if="field.type == 'yesno'">
                            <label>
                                <input type="radio" :id="field.id + '_yes'" v-model="profile.values[field.id]"
                                       :value="1" :disabled="isDisabled(field)">
                                <span>{{ $t('text.yes') }}</span>
                            </label>
                            <label>
                                <input type="radio" :id="field.id + '_no'" v-model="profile.values[field.id]" :value="0"
                                       :disabled="isDisabled(field)">
                                <span>{{ $t('text.no') }}</span>
                            </label>
                            <error :message="$v.message(field.id)"/>
                        </div>

                        <!-- DROPDOWN -->
                        <div
                            :class="['dropdown', 'dropdown--framed', isDisabled(field) ? 'disabled' : '', $v.hasError(field.id) ? 'error' : '']"
                            v-if="field.type == 'select'">
                            <span class="label">
                                <span v-html="label(field)"></span>
                                <icon name="info" v-tooltip="field.description[$lang]" v-if="field.description"/>
                            </span>
                            <div>
                                <icon name="chevron_down" viewBox="0 0 24 24"/>
                                {{ getSelectedValue(field) }}
                                <select :id="field.id" v-model="profile.values[field.id]" :disabled="isDisabled(field)">
                                    <option :value="key" v-for="(value, key) in getOptions(field)">{{ value }}</option>
                                </select>
                            </div>
                            <error :message="$v.message(field.id)"/>
                        </div>

                        <!-- NOTE -->
                        <div class="note"
                             v-if="$current.profile == 'Ryanair' && field.id == 'health_insurance_law' && profile.values[field.id] == 'unknown_uninsured' && ! mobile"
                             style="margin-top:20px">{{ $t('sko.malta.tk.0') }} <a
                            href="https://www.meinsternum.de/tk-airmalta" target="_blank">{{ $t('sko.malta.tk.1') }}</a>
                            {{ $t('sko.malta.tk.2') }} <a href="/static/Welcome-to-Germany.pdf"
                                                          target="_blank">{{ $t('sko.malta.tk.3') }}</a>.</a></div>
                    </div>
                </div><!-- end: .fieldset--inner -->
            </fieldset>

            <input type="submit" class="btn btn--green btn--profile-save" :value="$t('layout.footer.save')">
        </form>

        <!-- footer bar needs to be implemented on parent page! adjacent to "content" div! -->
    </div>
</template>

<script>
import events from '../../events/events';
import labels from '../lists/labels';
import countries from '../lists/countries';
import insurances from '../lists/insurances';
import Vue from "vue";

export default {
    name: 'Profile',

    props: ['endpoint', 'profile', 'employee'],

    data() {
        return {
            initialized: false,

            selection: {}, // stores v-select selection (object)
            uploads: {}, // stores uploaded files

            addDocument: false,
            addUpload: {},
            documentType: null, // the document type to be uploaded
            documentTypes: [],

            mobile: location.hostname.match('mobile') ? true : false,

            lists:
                {
                    labels: labels,
                    countries: countries,
                    insurances: insurances
                },
        }
    },

    created() {
        this.initialize();

        events.$emit('profile.changed', this.changed.length);
        events.$emit('profile.remaining', this.remaining.length);
        events.$emit('profile.filled', this.filled.length);

        events.$listen('profile.submit', this.submitForm);
        events.$listen('profile.reset', this.resetForm);
    },

    watch:
        {
            filled(val) {
                events.$emit('profile.filled', val.length);
            },

            remaining(val) {
                events.$emit('profile.remaining', val.length);
            },

            changed(val) {
                events.$emit('profile.changed', val.length);

                if (val.length) {
                    this.$store.dispatch('layout/showFooterBar');
                } else {
                    this.$store.dispatch('layout/hideFooterBar');
                }
            },
        },

    computed:
        {
            Vue() {
                return Vue
            },
            isManager() {
                return this.$onduro.hasRole('manager') && this.$route.name != 'profile';
            },

            fields() {
                return _.keyBy(_.flatten(_.map(this.profile.template, 'fields')), 'id');
            },

            required() {
                return _.filter(this.fields, field => {
                    return this.isRequired(field);
                });
            },

            remaining() {
                return _.filter(this.required, field => {
                    // only empty string, null or undefined is considered empty
                    // boolean (false) or integer 0 might be valid values
                    let empty = _.isNil(this.profile.values[field.id]) || this.profile.values[field.id] === '';

                    if (field.type == 'upload') {
                        empty = !this.isUploaded(field.id);
                    }

                    return this.isRequired(field) && empty;
                });
            },

            filled() {
                return _.difference(this.required, this.remaining);
            },

            changed() {
                let changed = [];

                _.forEach(this.fields, field => {
                    // stringify in order to make valid comparison of observed objects
                    if (JSON.stringify(this.profile.values[field.id]) != JSON.stringify(this.getDefaultValue(field))) changed.push(field.id);
                });

                return changed;
            },

            // get "hr_..." documents
            hrDocuments() {
                return _.pickBy(this.uploads, (files, key) => {
                    return key.indexOf('hr_') === 0;
                })
            },
        },

    methods:
        {
            initialize() {
                // make sure profile is loaded
                if (_.size(this.profile)) {
                    this.setDefaults();

                    this.documentTypes = this.profile.documentTypes;

                    // prevent multiple initializations
                    this.initialized = true;
                }
            },

            getClasses(field) {
                return [
                    'row',
                    field.class || null,
                    this.isRequired(field) ? 'required' : null,
                    this.isRequiredForEmployee(field) ? 'required-employee' : null,
                    this.isPristine(field) ? 'pristine' : null,
                    field.left || (field.type != 'checkbox' && this.label(field).length > 30) ? 'row--left' : null // move label to left if it's too long
                ];
            },

            resetForm() {
                this.setDefaults();
            },

            updateDate(date, id) {
                this.$set(this.profile.values, id, date);
            },

            isUploaded(key) {
                return (this.uploads[key]) ? true : false;
            },

            downloadFile(key, index = 0) {
                window.location.href = '/api/uploads/' + this.uploads[key][index].id + '?token=' + this.$store.state.auth.token;
            },

            uploadFile(e, field) {
                if (!this.profile.membership) {
                    this.$notify.error();
                    return;
                }

                let files = e.target.files || e.dataTransfer.files;

                if (!files.length) return;

                this.errorUpload = false;
                this.processing = true;

                let reader = new FileReader();
                reader.readAsDataURL(files[0]);

                let formData = new FormData();

                if (field === 'document') {
                    formData.append('key', this.documentType);
                    formData.append('type', 'hr');
                } else {
                    formData.append('key', field.id);
                }

                formData.append('file', files[0]);

                this.$http.post('/api/memberships/' + this.profile.membership + '/upload', formData)
                    .then(response => {
                        if (!this.uploads[response.data.key]) {
                            this.$set(this.uploads, response.data.key, []);
                        }

                        this.uploads[response.data.key].push(response.data)

                        this.documentType = null
                        this.addDocument = false
                        this.$set(this.addUpload, field.id, false)
                    })
                    .catch(error => {
                        if (error.response.status == 413) {
                            this.$notify.error(this.$t('error.general.title'), this.$t('error.413'));
                        } else {
                            // file upload failed
                            this.$notify.error();
                        }
                    });
            },

            deleteUpload(key, index = 0) {
                this.$http.delete('/api/uploads/' + this.uploads[key][index].id)
                    .then(response => {
                        // remove from uploads
                        this.$delete(this.uploads[key], index);

                        if (this.uploads[key].length === 0) {
                            this.$delete(this.uploads, key);
                        }
                    })
                    .catch(error => {
                        // file deletion failed
                        this.$notify.error();
                    });
            },

            submitForm() {
                let endpoint = this.endpoint ? this.endpoint : '/api/memberships/' + this.profile.membership + '/profile';

                this.$http.put(endpoint, this.profile.values)
                    .then(response => {
                        // set "default" data to current data
                        this.profile.data = this.$onduro.untrack(this.profile.values);

                        // emit "profile.stored" event
                        events.$emit('profile.stored', response.data);

                        this.$v.resetErrors();

                        this.$notify.success();
                    })
                    .catch(error => {
                        if (error.response.status == 422 && error.response.data) {
                            this.$notify.error(this.$t('error.general.invalid'), this.$t('error.general.check'));
                            this.$v.setErrors(error.response.data);
                        }
                    });
            },

            setDefaults() {
                _.forEach(this.fields, field => {
                    this.$set(this.profile.values, field.id, this.getDefaultValue(field));

                    if (field.type == 'auto') {
                        // set selection object
                        this.selection[field.id] = this.getSelection(field);
                    }
                });

                this.uploads = this.profile.uploads;
            },

            hasFields(fieldset) {
                let visible = 0;

                _.forEach(fieldset.fields, field => {
                    if (this.showField(field)) visible += 1;
                });

                return _.size(fieldset.fields) && visible; // minimum 1 visible field
            },

            label(field) {
                if (field.label) return field.label[this.$lang];
                return this.lists.labels[this.$lang][field.id] || '';
            },

            condition(field) {
                if (_.isArray(field.if)) {
                    let allConditionsMet = true;

                    // multiple conditions
                    _.forEach(field.if, condition => {
                        let fieldCopy = _.clone(field);

                        // set current condition as condition
                        fieldCopy.if = condition;

                        if (!this.condition(fieldCopy)) allConditionsMet = false;
                    });

                    return allConditionsMet;
                }

                switch (field.if.condition) {
                    case '!=':
                    case '<>':
                        return this.profile.values[field.if.field] != field.if.value;
                        break;

                    case '<':
                        return this.profile.values[field.if.field] < field.if.value;
                        break;

                    case '>':
                        return this.profile.values[field.if.field] > field.if.value;
                        break;

                    case '<=':
                        return this.profile.values[field.if.field] <= field.if.value;
                        break;

                    case '>=':
                        return this.profile.values[field.if.field] >= field.if.value;
                        break;

                    case 'in':
                        if (!this.profile.values[field.if.field]) return false;
                        return field.if.value.indexOf(this.profile.values[field.if.field]) != -1;
                        break;

                    case 'not_in':
                        if (!this.profile.values[field.if.field]) return false;
                        return field.if.value.includes(this.profile.values[field.if.field]) ? false : true;
                        break;

                    case 'exists':
                        if (this.profile.values[field.if.field]) return true;
                        break;

                    case 'empty':
                        if (!this.profile.values[field.if.field]) return true;
                        break;

                    case 'after_brexit':
                        if (this.profile.values['nationality'] != 'gb') return true;

                        if (this.profile.values[field.if.field] && this.profile.values[field.if.field] >= '2021-01-01') return true;

                        return false;
                        break;

                    default:
                        return this.profile.values[field.if.field] == field.if.value;
                }

                return false;
            },

            showField(field) {
                if (!this.may(field.id, 'read')) return false;
                if (!field.if || field.show) return true;

                return this.condition(field);
            },

            may(id, action) {
                return this.fields[id].rights[action];
            },

            isLockable(field) {
                return inArray(field.id, ['fname', 'mname', 'lname_suffix', 'lname', 'email', 'address', 'address_extra', 'zip', 'city', 'phone', 'mobile', 'fax']);
            },

            isPristine(field) {
                // field is not pristine, if filled
                if (this.profile.values[field.id] && this.profile.values[field.id] !== null) return false;

                if (field.type == 'upload') {
                    if (this.isUploaded(field.id)) return false;
                }

                // otherwise it is pristine, if default is empty string or null
                return this.profile.values[field.id] === '' || this.profile.values[field.id] === null || typeof this.profile.values[field.id] === 'undefined';
            },

            isReadonly(field) {
                if (field.id == 'personal_note' && !this.may(field.id, 'update')) {
                    return true;
                }

                return false;
            },

            isDisabled(field) {
                if (field.id == 'personal_note') return false;

                // do not allow to change e-mail adress in profile if employee has access
                if (field.id == 'email' && this.employee && this.employee.access == 'yes' && this.$current.membership.id != this.profile.membership) {
                    return true;
                }

                // has data been stored before?
                if (!this.profile.data[field.id] || this.profile.data[field.id] === null) {
                    // disable, if user may not create
                    return !this.may(field.id, 'create');
                }

                // allow to 'update' already populated name fields if user is not locked
                if (this.employee && !this.employee.locked && this.isLockable(field) && this.may(field.id, 'update')) {
                    return false;
                }

                // this is user's own profile
                if (this.$current.membership.id == this.profile.membership && inArray(field.id, ['fname', 'lname', 'email', 'birthday'])) {
                    return false;
                }

                // allow to update if user has no access
                if (this.employee.access == 'no') {
                    return false;
                }

                // otherwise disable, if user may not update
                return !this.may(field.id, 'update');
            },

            isRequired(field) {
                let req = (field.required_if && this.condition(field)) ? field.required_if : field.required;

                if (this.isManager) {
                    // required fields for employer
                    return req.employer && this.showField(field) && !this.isDisabled(field);
                }

                // required fields for employee
                return this.isRequiredForEmployee(field);
            },

            isRequiredForEmployee(field) {
                let req = (field.required_if && this.condition(field)) ? field.required_if : field.required;

                return (req.employer || req.employee) && this.showField(field) && !this.isDisabled(field);
            },

            showLabel(field) {
                return ['select', 'country', 'checkbox'].indexOf(field.type) == -1;
            },

            getDefaultValue(field) {
                if (this.profile.data[field.id] != null) return this.profile.data[field.id];
                if (field.default != null) return field.default;
                return null;
            },

            getSelectedValue(field) {
                return this.getOptions(field)[this.profile.values[field.id]];
            },

            getOptions(field) {
                if (_.isString(field.options)) {
                    // For birth_country field, translate country names based on current language
                    if ((field.id === 'birth_country' && field.options === 'countries') || (field.id === 'country' && field.options === 'countries')) {
                        return this.getTranslatedCountries();
                    }
                    return this.lists[field.options];
                } else {
                    return field.options;
                }
            },

            getTranslatedCountries() {
                // Get countries list
                const countriesList = this.lists.countries;

                // If current language is English, return the original list
                if (this.$lang === 'en') {
                    return countriesList;
                }

                // For other languages, try to translate country names using i18n
                const translatedCountries = {};

                for (const [code, name] of Object.entries(countriesList)) {
                    // Try to get translation from i18n
                    const translationKey = `common.countries.${code}`;
                    const translation = this.$t(translationKey);

                    // If translation exists and is not the same as the key, use it
                    if (translation !== translationKey) {
                        translatedCountries[code] = translation;
                    } else {
                        // Fallback to original name
                        translatedCountries[code] = name;
                    }
                }

                return translatedCountries;
            },

            setValue(field) {
                this.profile.values[field.id] = (this.selection[field.id]) ? this.selection[field.id].value : null;
            },

            getSelection(field) {
                return _.find(this.getCollection(field), {value: this.profile.values[field.id]});
            },

            getCollection(field) {
                let collection = [];

                _.forEach(this.getOptions(field), (val, key) => {
                    collection.push({
                        label: val,
                        value: key,
                    });
                });

                collection = _.sortBy(collection, 'label');

                let selection = _.find(collection, {value: this.profile.values[field.id]});

                return _.differenceBy(collection, selection, 'value');
            },

            getDocumentKey(groupKey, docKey) {
                return 'hr_' + groupKey + '-' + docKey
            },

            getDocumentGroup(input) {
                if (!input) return input

                let keys = input.replace('hr_', '').split('-')

                return _.find(this.documentTypes, group => group.key == keys[0]).label
            },

            getDocumentValue(input) {
                if (!input) return input

                let keys = input.replace('hr_', '').split('-')

                let group = _.find(this.documentTypes, group => group.key == keys[0])

                return group ? _.find(group.documents, doc => doc.key == keys[1]).label : ''
            },

            cancelDocumentUpload() {
                this.documentType = null
                this.addDocument = false
                this.addUpload = {}
            },
        }
}
</script>



// WEBPACK FOOTER //
// Profile.vue?614caf19